import gql from 'graphql-tag'
import throttle from 'lodash/throttle'
import { print } from 'graphql/language/printer'

import FilesAPI from '../files'

import { basicResponseHandler, fetchBase, graphQLBase, makeBody } from '..'

export const userPresenceFragment = `
  fragment userPresence on UserPresence {
    id
    __typename
    updatedAt
    wroteAt
    noteId
    isWriting
    blockId
  }
`

export const userAttrsFragment = `
  fragment userAttrs on User {
    id
    __typename
    createdAt
    updatedAt
    archivedAt
    ssoEnabledAt
    oauthProvider
    hasOauth
    email
    username
    displayName
    photoURL
    organizationRole
    inviterId
    invitePending
    inviteCreatedAt
    inviteAcceptedAt
    intercomIdentifierHash
  }
`

export const showMyOrganizationUsersQuery = gql`
  query showMyOrganizationUsers(
    $pagination: PaginationInput!
    $includeArchived: Boolean!
    $order: UserOrder
  ) {
    showMyOrganizationUsers(
      input: {
        pagination: $pagination
        includeArchived: $includeArchived
        order: $order
      }
    ) {
      totalCount
      edges {
        cursor
        node {
          ...userAttrs
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${userAttrsFragment}
`

export const userGroupAttrsFragment = gql`
  fragment userGroupAttrs on UserGroup {
    __typename
    id
    name
    members {
      id
      __typename
      totalCount
      nodes {
        ...userAttrs
      }
    }
  }
  ${userAttrsFragment}
`

export const userAttrsWithPresenceFragment = `
  fragment userAttrsWithPresence on User {
    ...userAttrs
    presence {
      ...userPresence
    }
  }
  ${userAttrsFragment}
  ${userPresenceFragment}
`

export const userPreferencesAttrsFragment = gql`
  fragment userPreferencesAttrs on UserPreferences {
    id
    __typename
    favoriteChannelIds
    bookmarkedNoteIds
    lastVisitedNoteIds
    lastVisitedChannelIds
    seenBannerDownloadDesktopApp
    featureFlags
    notificationsDigestFrequency
    mutedChannelIds
    onboardingStep
    isChannelOnboardingDone
    lastActivityFeedViewedAt
    acceptedPrivacyPolicyAt
    seenNearNoteLimitIncentiveAt
    closedImportContentCallToActionAt
    defaultChannelsTab
    onboardingChannelId
    suppressedEnableApp
    suppressedConnectApp
    isDesktopUser
    confluenceApiAuthentication
    colorMode
  }
`

export const updateUserPreferences = gql`
  mutation UpdateUserPreferences(
    $seenBannerDownloadDesktopApp: Boolean
    $favoriteChannelIds: [ID]
    $mutedChannelIds: [ID]
    $featureFlags: String
    $notificationsDigestFrequency: NotificationsDigestFrequency
    $onboardingStep: String
    $bookmarkedNoteIds: [ID]
    $isChannelOnboardingDone: Boolean
    $lastActivityFeedViewedAt: Date
    $acceptedPrivacyPolicyAt: Date
    $seenNearNoteLimitIncentiveAt: Date
    $closedImportContentCallToActionAt: Date
    $defaultChannelsTab: [DefaultChannelsTabInput!]
    $suppressedEnableApp: [ID!]
    $suppressedConnectApp: [ID!]
    $isDesktopUser: Boolean
    $confluenceApiAuthentication: ConfluenceApiAuthenticationInput
    $colorMode: ColorModePreference
  ) {
    updateUserPreferences(
      seenBannerDownloadDesktopApp: $seenBannerDownloadDesktopApp
      favoriteChannelIds: $favoriteChannelIds
      featureFlags: $featureFlags
      mutedChannelIds: $mutedChannelIds
      bookmarkedNoteIds: $bookmarkedNoteIds
      notificationsDigestFrequency: $notificationsDigestFrequency
      onboardingStep: $onboardingStep
      isChannelOnboardingDone: $isChannelOnboardingDone
      lastActivityFeedViewedAt: $lastActivityFeedViewedAt
      acceptedPrivacyPolicyAt: $acceptedPrivacyPolicyAt
      seenNearNoteLimitIncentiveAt: $seenNearNoteLimitIncentiveAt
      closedImportContentCallToActionAt: $closedImportContentCallToActionAt
      defaultChannelsTab: $defaultChannelsTab
      suppressedEnableApp: $suppressedEnableApp
      suppressedConnectApp: $suppressedConnectApp
      isDesktopUser: $isDesktopUser
      confluenceApiAuthentication: $confluenceApiAuthentication
      colorMode: $colorMode
    ) {
      ...userPreferencesAttrs
    }
  }
  ${userPreferencesAttrsFragment}
`

export const getMeQuery = gql`
  query getMe {
    me {
      ...userAttrs
      abFlags
    }
    getUsersNotifications {
      id
      __typename
      unreadNotificationsCount
    }
    showUserPreferences {
      ...userPreferencesAttrs
    }
  }
  ${userAttrsFragment}
  ${userPreferencesAttrsFragment}
`

const UsersAPI = {
  updateUser: graphQLBase(
    `mutation UpdateCurrentUser($username: String, $displayName: String, $position: String, $photoURL: String) {
      updateCurrentUser(username: $username, displayName: $displayName, position: $position, photoURL: $photoURL) {
        ...userAttrs
      }
    }
    ${userAttrsFragment}
    `,
    ({ updateCurrentUser }) => updateCurrentUser
  ),

  updateUserPresence: graphQLBase(
    `mutation UpdateUserPresence($noteId: ID, $wroteAt: Date, $blockId: ID, $isWriting: Boolean) {
      updateUserPresence(
        noteId: $noteId,
        wroteAt: $wroteAt,
        blockId: $blockId,
        isWriting: $isWriting
      ) {
          ...userAttrsWithPresence
        }
      }
      ${userAttrsWithPresenceFragment}
    `
  ),

  updateUserEmail({ apiEndpoint, apiToken }, userId, email, password) {
    return fetch(`${apiEndpoint}/users/${userId}/email`, {
      ...fetchBase({ apiToken }),
      method: 'PUT',
      body: makeBody('users', { email, password }),
    }).then(basicResponseHandler)
  },

  updateUserOrganizationRole(
    { apiEndpoint, apiToken },
    userId,
    organizationRole
  ) {
    return fetch(`${apiEndpoint}/users/${userId}/organizationRole`, {
      ...fetchBase({ apiToken }),
      method: 'PUT',
      body: makeBody('users', { organizationRole }, userId),
    }).then(basicResponseHandler)
  },

  archiveUser: gql`
    mutation archiveUser($userId: ID!) {
      archiveUser(userId: $userId) {
        ...userAttrs
      }
    }
    ${userAttrsFragment}
  `,

  backupAndArchiveMe: graphQLBase(
    `mutation backupAndArchiveMe {
      backupAndArchiveMe
    }`,
    ({ backupAndArchiveMe }) => backupAndArchiveMe
  ),

  fetchUser({ apiEndpoint, apiToken }, userId) {
    return fetch(`${apiEndpoint}/users/${userId}`, {
      ...fetchBase({ apiToken }),
    }).then(basicResponseHandler)
  },

  fetchMe: graphQLBase(print(getMeQuery)),

  updateUserPhotoURL(apiConfig, { photoFile }) {
    return FilesAPI.uploadFile(apiConfig, {
      file: photoFile,
      folder: 'avatars',
    }).then(photoURL => UsersAPI.updateUser(apiConfig, { photoURL }))
  },

  getGoogleDriveAuthToken: graphQLBase(
    `query getGoogleDriveAuthToken {
      showUserPreferences {
        googleDriveAccessToken
      }
    }`,
    ({ showUserPreferences }) => showUserPreferences.googleDriveAccessToken
  ),
  setUserAsOwner: gql`
    mutation SetUserAsOwner($userId: ID!) {
      setUserAsOwner(userId: $userId) {
        success
      }
    }
  `,
  updateUserPreferences: graphQLBase(print(updateUserPreferences)),
}

export const goToUserOnboardingStep = gql`
  mutation GoToUserOnboardingStep($step: String) {
    goToUserOnboardingStep(step: $step) {
      ...userPreferencesAttrs
    }
  }
  ${userPreferencesAttrsFragment}
`

export const skipUserOnboarding = gql`
  mutation SkipUserOnboarding {
    skipUserOnboarding {
      ...userPreferencesAttrs
    }
  }
  ${userPreferencesAttrsFragment}
`

export const throttledUpdateUserPresence = throttle(
  UsersAPI.updateUserPresence,
  10 * 1000 /* 10s */,
  { leading: false, trailing: true }
)

export default UsersAPI
